import { AlertCircle, Calendar, ChevronRight } from 'lucide-react';
import type { BottleneckReportContent, FreeResult } from '../../lib/apiClient';
import {
  BOTTLENECK_EXPLANATIONS,
  BOTTLENECK_LABELS,
} from '../../lib/report/bottleneckCategories';
import { computePrioritySignals } from '../../lib/report/prioritySignals';
import { generateWhatChangedNarrative } from '../../lib/report/whatChanged';
import { ReportHero } from './ReportHero';
import { FieldLabel, LabelledPanel, PromptList, ReportBlock, SectionHeader } from './reportSections';

/**
 * The full paid Bottleneck report, copied from production's `report.tsx`.
 *
 * **Production numbers its sections and V2 did not.** The report is a document
 * a customer works through — Deep Dive, Stop/Start, the 7-day journal, the
 * 30-day workbook, Reflection & Habits, Warning Signals — and the numbering is
 * how they keep their place in it. V2 rendered eleven flat headings in a
 * different order and split the narrative differently.
 *
 * The narrative fields are the ones the API already returns; nothing new is
 * fetched. What changed is the structure around them, which is production's.
 */
export function BottleneckReport({
  result,
  content,
  firstName = null,
  previousResult = null,
}: {
  result: FreeResult;
  content: BottleneckReportContent;
  /** Production personalises the hero eyebrow when it knows the name. */
  firstName?: string | null;
  /** The caller's previous result of this type, when there is one. */
  previousResult?: FreeResult | null;
}) {
  const signals = computePrioritySignals({
    bottleneckScore: result.bottleneckScore ?? 0,
    executionReadinessScore: result.executionReadinessScore ?? 0,
    recoveryDeficitScore: result.recoveryDeficitScore ?? 0,
    clarityScore: result.clarityScore ?? 0,
  });

  /* Production ranks every category by friction, highest first. */
  const categoryBars = Object.entries(result.categoryScores)
    .map(([key, score]) => ({
      key,
      label: BOTTLENECK_LABELS[key] ?? key,
      pct: Math.round((score / 5) * 100),
    }))
    .sort((a, b) => b.pct - a.pct);

  const topAreas = result.topCategories.filter((c) => BOTTLENECK_EXPLANATIONS[c]);

  /*
    Production's retake comparison. It needs both score sets and the earlier
    completion date; a first-time taker has no previous result and gets no
    section, which is production's behaviour rather than an empty state.
  */
  const whatChanged =
    previousResult && previousResult.completedAt
      ? generateWhatChangedNarrative(
          {
            bottleneckScore: result.bottleneckScore ?? 0,
            executionReadinessScore: result.executionReadinessScore ?? 0,
            recoveryDeficitScore: result.recoveryDeficitScore ?? 0,
            clarityScore: result.clarityScore ?? 0,
            primaryType: result.primaryType,
          },
          {
            bottleneckScore: previousResult.bottleneckScore ?? 0,
            executionReadinessScore: previousResult.executionReadinessScore ?? 0,
            recoveryDeficitScore: previousResult.recoveryDeficitScore ?? 0,
            clarityScore: previousResult.clarityScore ?? 0,
            primaryType: previousResult.primaryType,
            completedAt: previousResult.completedAt,
          },
        )
      : null;

  return (
    <div className="flex flex-col gap-14" data-testid="full-report-bottleneck">
      <ReportHero result={result} firstName={firstName} whatThisMeans={content.whatThisMeans} />

      {/*
        Priority Signals. Production shows no section at all when nothing is
        elevated, which is why an empty list is a real outcome here.
      */}
      {signals.length > 0 ? (
        <section className="flex flex-col gap-3" data-testid="report-priority-signals">
          <SectionHeader label="Your Data" title="Priority Signals" />
          <div className="flex flex-col gap-3">
            {signals.map((signal) => (
              <div
                key={signal.id}
                className="flex gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4"
                data-testid="priority-signal"
                data-signal={signal.id}
              >
                <AlertCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-amber-500"
                  aria-hidden="true"
                />
                <p className="text-sm leading-relaxed">{signal.text}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {categoryBars.length > 0 ? (
        <section className="flex flex-col gap-4" data-testid="report-category-breakdown">
          <SectionHeader label="Your Data" title="Category Breakdown" />
          <p className="text-sm text-muted-foreground">
            Your friction score across all eight bottleneck categories. Higher means that pattern is
            more active in your current performance system.
          </p>
          <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6">
            {categoryBars.map((bar) => (
              <div key={bar.key} className="flex flex-col gap-1" data-category={bar.key}>
                <div className="flex items-baseline justify-between text-sm">
                  <span>{bar.label}</span>
                  <span className="tabular-nums text-muted-foreground">{bar.pct}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                  {/* Production's thresholds: 70 and above red, 50 amber, else green. */}
                  <div
                    className={`h-full rounded-full ${
                      bar.pct >= 70
                        ? 'bg-red-500'
                        : bar.pct >= 50
                          ? 'bg-amber-500'
                          : 'bg-green-500'
                    }`}
                    style={{ width: `${bar.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {whatChanged ? (
        <section className="flex flex-col gap-4" data-testid="report-what-changed">
          <SectionHeader label="Since Your Last Assessment" title="What Changed" />
          <div className="rounded-xl border border-border bg-card p-6">
            <p className="text-sm leading-relaxed text-muted-foreground">{whatChanged}</p>
          </div>
        </section>
      ) : null}

      {/* Production chips the customer's top bottlenecks under the hero. */}
      {result.topCategories.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="report-top-chips">
          <span className="self-center text-xs text-muted-foreground">Top bottlenecks:</span>
          {result.topCategories.map((category) => (
            <span
              key={category}
              className="rounded-full border border-border bg-secondary px-3 py-1 text-xs font-semibold"
            >
              {BOTTLENECK_LABELS[category] ?? category}
            </span>
          ))}
        </div>
      ) : null}

      {topAreas.length > 0 ? (
        <section className="flex flex-col gap-4" data-testid="report-top-areas">
          <SectionHeader label="Your Data" title="Your Top Bottleneck Areas" />
          <p className="text-sm text-muted-foreground">
            What each of your active bottleneck categories means for performance.
          </p>
          <div className="flex flex-col gap-3">
            {topAreas.map((category) => (
              <div
                key={category}
                className="rounded-xl border border-border bg-card p-5"
                data-testid="top-area"
                data-category={category}
              >
                <h3 className="text-sm font-semibold">{BOTTLENECK_LABELS[category] ?? category}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {BOTTLENECK_EXPLANATIONS[category]}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Section 01 ─────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-6">
        <SectionHeader label="Section 01" title="Deep Dive" />

        <ReportBlock title="What This Pattern Looks Like">{content.fullExplanation}</ReportBlock>

        <div className="grid gap-5 md:grid-cols-2">
          <ReportBlock title="Why This Develops">{content.whyThisDevelops}</ReportBlock>
          <ReportBlock title="The Hidden Cost">{content.hiddenCost}</ReportBlock>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <ReportBlock title="The False Belief" variant="accent">
            {content.falseBelief}
          </ReportBlock>
          <ReportBlock title="How You Sabotage Progress" variant="accent">
            {content.sabotagePattern}
          </ReportBlock>
        </div>

        <ReportBlock title="What You Need Most Right Now" variant="highlight">
          {content.whatYouNeedMost}
        </ReportBlock>

        {/* Production sets this one apart entirely: a rule down the side, italic. */}
        <div className="rounded-xl border-l-4 border-primary bg-primary/5 p-7" data-testid="report-truth">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-primary">
            The Truth You Need to Hear
          </p>
          <p className="text-base italic leading-relaxed">{content.truthToHear}</p>
        </div>

        <ReportBlock title="What Changes When You Address This">{content.whatChanges}</ReportBlock>
      </section>

      {/* ── Section 02 ─────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-5">
        <SectionHeader label="Section 02" title="Stop Doing / Start Doing" />
        <div className="grid gap-5 md:grid-cols-2">
          <LabelledPanel label="Stop Doing" labelClass="text-red-500" testId="report-stop-doing">
            <ul className="flex flex-col gap-2.5">
              {content.stopDoing.map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <span className="mt-0.5 shrink-0 text-xs font-bold text-red-500" aria-hidden="true">
                    ✕
                  </span>
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </LabelledPanel>

          <LabelledPanel label="Start Doing" labelClass="text-green-500" testId="report-start-doing">
            <ul className="flex flex-col gap-2.5">
              {content.startDoing.map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <span
                    className="mt-0.5 shrink-0 text-xs font-bold text-green-500"
                    aria-hidden="true"
                  >
                    ✓
                  </span>
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </LabelledPanel>
        </div>
      </section>

      {/* ── Section 03 ─────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-5">
        <SectionHeader label="Section 03" title="7-Day Reset Journal" />
        <p className="text-sm text-muted-foreground">
          One day at a time. Complete each day before moving to the next.
        </p>
        <div className="flex flex-col gap-4">
          {content.sevenDayPlan.map((day) => (
            <div
              key={day.day}
              className="overflow-hidden rounded-xl border border-border bg-card"
              data-testid="report-day"
              data-day={day.day}
            >
              <div className="flex items-baseline gap-3 border-b border-border bg-secondary/30 px-6 py-4">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  Day {day.day}
                </span>
                <h4 className="text-base font-bold">{day.focus}</h4>
              </div>
              <div className="flex flex-col gap-4 p-6">
                <div>
                  <FieldLabel>Objective</FieldLabel>
                  <p className="text-sm font-semibold">{day.objective}</p>
                </div>
                <div>
                  <FieldLabel>Coaching Note</FieldLabel>
                  <p className="text-sm leading-relaxed text-muted-foreground">{day.coachingNote}</p>
                </div>
                <div>
                  <FieldLabel>Actions</FieldLabel>
                  <ul className="flex flex-col gap-1.5">
                    {day.actions.map((action) => (
                      <li key={action} className="flex items-start gap-2 text-sm">
                        <ChevronRight
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
                          aria-hidden="true"
                        />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-t border-border pt-3">
                  <FieldLabel>Journal Prompts</FieldLabel>
                  <PromptList items={day.journalPrompts} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Section 04 ─────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-5">
        <SectionHeader label="Section 04" title="30-Day Reset Workbook" />
        <div className="flex flex-col gap-4">
          {content.thirtyDayPlan.map((week) => (
            <div
              key={week.week}
              className="overflow-hidden rounded-xl border border-border bg-card"
              data-testid="report-week"
              data-week={week.week}
            >
              <div className="border-b border-border bg-secondary/30 px-6 py-4">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  Week {week.week}
                </span>
                <h4 className="text-base font-bold">{week.theme}</h4>
              </div>
              <div className="grid gap-5 p-6 md:grid-cols-3">
                <div>
                  <FieldLabel>Goals</FieldLabel>
                  <ul className="flex flex-col gap-1.5">
                    {week.goals.map((goal) => (
                      <li key={goal} className="flex items-start gap-2 text-sm">
                        <ChevronRight
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
                          aria-hidden="true"
                        />
                        {goal}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <FieldLabel>Key Behaviors</FieldLabel>
                  <ul className="flex flex-col gap-1.5">
                    {week.behaviors.map((behavior) => (
                      <li key={behavior} className="flex items-start gap-2 text-sm">
                        <span className="mt-0.5 shrink-0 text-xs text-green-500" aria-hidden="true">
                          ✓
                        </span>
                        {behavior}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <FieldLabel>Journal Prompts</FieldLabel>
                  <ul className="flex flex-col gap-2">
                    {week.journalPrompts.map((prompt) => (
                      <li key={prompt} className="text-sm italic leading-relaxed text-muted-foreground">
                        {prompt}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Section 05 ─────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-5">
        <SectionHeader label="Section 05" title="Reflection &amp; Habits" />
        <div className="grid gap-5 md:grid-cols-2">
          <LabelledPanel label="Reflection Prompts" testId="report-reflection">
            <PromptList items={content.reflectionPrompts} />
          </LabelledPanel>
          <LabelledPanel label="Recommended Habits" testId="report-habits">
            <ul className="flex flex-col gap-2.5">
              {content.recommendedHabits.map((habit) => (
                <li key={habit} className="flex items-start gap-2 text-sm">
                  <ChevronRight
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
                    aria-hidden="true"
                  />
                  {habit}
                </li>
              ))}
            </ul>
          </LabelledPanel>
        </div>
      </section>

      {/* ── Section 06 ─────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-4">
        <SectionHeader label="Section 06" title="Warning Signals" />
        <p className="text-sm text-muted-foreground">
          Watch for these patterns. They signal the old behavior is reasserting itself.
        </p>
        <div className="rounded-xl border border-border bg-card p-6">
          <ul className="flex flex-col gap-2.5" data-testid="report-warnings">
            {content.warningSignals.map((signal) => (
              <li key={signal} className="flex items-start gap-2.5 text-sm">
                <AlertCircle
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500"
                  aria-hidden="true"
                />
                {signal}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section
        className="flex flex-col gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-8 md:p-10"
        data-testid="report-next-step"
      >
        <p className="text-xs font-bold uppercase tracking-widest text-primary">
          Your Next Best Step
        </p>
        <p className="text-base font-medium leading-relaxed">{content.nextBestStep}</p>
      </section>

      {/*
        Production's reassessment card. The control is disabled there too — the
        feature is not built — and the copy says so. A working button would be
        an improvement, and improvements come after parity.
      */}
      <section
        className="overflow-hidden rounded-2xl border border-border bg-card"
        data-testid="report-reassessment"
      >
        <div className="flex flex-col items-center gap-4 p-8 text-center md:p-10">
          <Calendar className="h-8 w-8 text-primary" aria-hidden="true" />
          <h3 className="text-xl font-bold">Come Back in 30 Days</h3>
          <p className="mx-auto max-w-md text-sm leading-relaxed text-muted-foreground">
            After completing this workbook, reassess your performance bottleneck. Track what
            changed, see where you have grown, and identify your next bottleneck at a discounted
            rate.
          </p>
          <button
            type="button"
            disabled
            className="cursor-not-allowed rounded-md border border-border px-4 py-2 text-sm opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            30-Day Reassessment. Coming Soon
          </button>
        </div>
      </section>
    </div>
  );
}
